import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { CrmSettingsContext } from './CrmLayout';
import { shortDate, teamColorStyle } from './constants';
import Compose from './Compose';
import MailboxSetup from './MailboxSetup';
import './Screens.css';
import './Mail.css';

// The Email section: Inbox, Sent, Compose, Mailbox.
//
// Once a rep connects their mailbox this is a real mail client -- their mail arrives on
// its own within seconds, files itself against the account it belongs to, and replies go
// back out as them. Until they connect one, the inbox says so plainly rather than showing
// a fake list.

const TABS = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'sent', label: 'Sent' },
  { key: 'compose', label: 'Compose' },
  { key: 'mailbox', label: 'Mailbox' },
];

// New mail is ingested by the mail worker the moment it lands, so the only thing left
// between the mail server and the screen is this. Short enough to feel live, long enough
// that a screen left open all day is not a load problem.
const REFRESH_MS = 20000;

const initials = (a, b) => `${(a || '').charAt(0)}${(b || '').charAt(0)}`.toUpperCase() || '@';

const senderOf = (m) => m.from_name || m.from_email || 'Unknown sender';
const recipientOf = (m) => {
  const to = Array.isArray(m.to_emails) ? m.to_emails : [];
  return to.length > 1 ? `${to[0]} +${to.length - 1}` : (to[0] || '—');
};

const MessageRow = ({ message, folder, useTeamColors, onOpen }) => {
  const colors = useTeamColors ? teamColorStyle(message) : null;
  const unread = folder === 'inbox' && !message.is_read;
  const who = folder === 'inbox' ? senderOf(message) : recipientOf(message);

  return (
    <li className={unread ? 'unread' : ''}>
      <button type="button" className="msg-row" onClick={() => onOpen(message)}>
        <span className="msg-avatar">
          {folder === 'inbox'
            ? initials(message.from_name || message.from_email, message.from_name ? message.from_name.split(' ')[1] : '')
            : '↗'}
        </span>
        <span className="msg-body">
          <span className="msg-top">
            <span className="msg-who">{who}</span>
            <span className="msg-when">{shortDate(message.occurred_at)}</span>
          </span>
          <span className="msg-subject">{message.subject || '(no subject)'}</span>
          <span className="msg-snippet">{message.snippet}</span>
          {message.organization_name && (
            <span className="msg-account">
              {colors && <span className="team-chip" style={colors} aria-hidden="true" />}
              {message.organization_name}
            </span>
          )}
        </span>
      </button>
    </li>
  );
};

const ReplyBox = ({ message, onSent }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const replyTo = message.direction === 'inbound'
    ? (message.from_email || '')
    : ((Array.isArray(message.to_emails) ? message.to_emails[0] : '') || '');

  const send = async () => {
    setBusy(true); setError(''); setNote('');
    try {
      const r = await api.post(`/crm/messages/${message.id}/reply`, { body });
      setBody('');
      // A reply that went out but could not be filed in the rep's Sent folder is still
      // sent. Saying "failed" here would have them send it twice.
      setNote(r.data.savedToSentFolder
        ? `Sent to ${r.data.sent.join(', ')}.`
        : `Sent to ${r.data.sent.join(', ')} — but it could not be copied to your Sent folder.`);
      onSent();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send that reply');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="msg-reply">
      <label htmlFor="crm-reply">Reply to {replyTo}</label>
      <textarea
        id="crm-reply"
        rows={6}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply…"
      />
      {error && <p className="banner error">{error}</p>}
      {note && <p className="banner mbx-note">{note}</p>}
      <div className="msg-reply-actions">
        <button type="button" className="btn primary" onClick={send} disabled={busy || !body.trim()}>
          {busy ? 'Sending…' : 'Send reply'}
        </button>
        <span className="muted">Goes out from your own address, threaded to this message.</span>
      </div>
    </div>
  );
};

const MessageView = ({ message, onClose, onNavigateAccount, canReply, onReplied }) => (
  <div className="msg-detail">
    <button type="button" className="msg-back" onClick={onClose}>← Back</button>
    <h2>{message.subject || '(no subject)'}</h2>
    <div className="msg-meta">
      <span>
        <strong>{message.direction === 'inbound' ? senderOf(message) : (message.sent_by_first_name || 'You')}</strong>
        {message.direction === 'inbound' ? '' : ' sent'} · {shortDate(message.occurred_at)}
      </span>
      <span className="muted">
        {message.direction === 'inbound'
          ? `to ${message.mailbox_address || 'the CRM mailbox'}`
          : `to ${recipientOf(message)}`}
      </span>
      {message.organization_name && (
        <button type="button" className="msg-account-link" onClick={() => onNavigateAccount(message.organization_id)}>
          {message.organization_name}
        </button>
      )}
    </div>
    <div className="msg-content">
      {message.body_text
        ? message.body_text.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)
        : <p className="muted">No message body stored.</p>}
    </div>
    {canReply
      ? <ReplyBox message={message} onSent={onReplied} />
      : (
        <p className="msg-reply-locked">
          Connect your mailbox on the <strong>Mailbox</strong> tab to reply from here as yourself.
        </p>
      )}
  </div>
);

const Mail = () => {
  const navigate = useNavigate();
  const { settings } = useContext(CrmSettingsContext);
  const [tab, setTab] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMailboxes = useCallback(async () => {
    try {
      const r = await api.get('/crm/mailboxes');
      setMailboxes(r.data.mailboxes);
    } catch {
      setMailboxes([]);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    if (tab === 'compose' || tab === 'mailbox') return;
    try {
      setLoading(true);
      const params = new URLSearchParams({ folder: tab });
      if (search) params.append('search', search);
      if (unreadOnly && tab === 'inbox') params.append('unread', 'true');
      const r = await api.get(`/crm/messages?${params.toString()}`);
      setMessages(r.data.messages);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load mail');
    } finally {
      setLoading(false);
    }
  }, [tab, search, unreadOnly]);

  useEffect(() => { fetchMailboxes(); }, [fetchMailboxes]);
  useEffect(() => {
    const t = setTimeout(fetchMessages, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchMessages, search]);

  // Keep the list current on its own. The worker has already put new mail in the
  // database by the time this fires, so a rep watching the screen sees it arrive
  // without touching anything. Reading a message pauses it -- nothing should reshuffle
  // underneath someone mid-read.
  useEffect(() => {
    if (tab === 'compose' || tab === 'mailbox' || open) return undefined;
    const t = setInterval(() => { fetchMessages(); fetchMailboxes(); }, REFRESH_MS);
    return () => clearInterval(t);
  }, [tab, open, fetchMessages, fetchMailboxes]);

  const openMessage = async (row) => {
    try {
      const r = await api.get(`/crm/messages/${row.id}`);
      setOpen(r.data.message);
      if (!row.is_read && row.direction === 'inbound') {
        await api.patch(`/crm/messages/${row.id}`, { is_read: true });
        setMessages(prev => prev.map(m => (m.id === row.id ? { ...m, is_read: true } : m)));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open that message');
    }
  };

  const personal = mailboxes.find(m => m.user_id);
  const connected = personal && personal.status === 'connected';
  // Replying needs a working way out as well as a connection -- a mailbox can be
  // connected for reading and still not be able to send. Either transport counts:
  // SMTP submission where the server allows it, Exchange Web Services where it does not.
  const canSend = !!(connected && (personal.smtp_host || personal.ews_url));

  return (
    <div className="crm-screen crm-mail">
      <div className="screen-header">
        <div>
          <h1>Email</h1>
          <p className="subtitle">
            Mail tied to your accounts. Sending goes out individually — no recipient sees another.
          </p>
        </div>
      </div>

      <div className="mail-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            className={`mail-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => { setTab(t.key); setOpen(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="banner error">{error}</p>}

      {tab !== 'compose' && tab !== 'mailbox' && !connected && (
        <div className="mailbox-status">
          <span className="dot" aria-hidden="true" />
          <div>
            <strong>No personal mailbox connected</strong>
            <p>
              The CRM sends through HUB's shared mailbox, so replies land in your real inbox rather
              than here. Connect your own mailbox and your mail arrives on this screen within
              seconds, files itself against the right account, and you can reply as yourself.
            </p>
            <button type="button" className="btn primary" onClick={() => setTab('mailbox')}>
              Connect my mailbox
            </button>
          </div>
        </div>
      )}

      {tab === 'mailbox' ? (
        <MailboxSetup mailboxes={mailboxes} onChanged={() => { fetchMailboxes(); fetchMessages(); }} />
      ) : tab === 'compose' ? (
        <Compose
          sentFrom={canSend ? personal.email_address : null}
          onConnectMailbox={() => setTab('mailbox')}
          onSent={() => { setTab('sent'); fetchMessages(); }}
        />
      ) : open ? (
        <MessageView
          message={open}
          onClose={() => setOpen(null)}
          onNavigateAccount={(id) => navigate(`/crm/organizations/${id}`)}
          canReply={canSend}
          onReplied={() => { fetchMessages(); }}
        />
      ) : (
        <>
          <div className="filters">
            <div className="grow">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tab === 'inbox' ? 'Search sender, subject, or account…' : 'Search recipient, subject, or account…'}
              />
            </div>
            {tab === 'inbox' && (
              <button
                type="button"
                className={`btn${unreadOnly ? ' primary' : ''}`}
                onClick={() => setUnreadOnly(v => !v)}
              >
                {unreadOnly ? 'Showing unread' : 'Unread only'}
              </button>
            )}
          </div>

          {loading && messages.length === 0 ? (
            <p className="loading">Loading…</p>
          ) : messages.length === 0 ? (
            <div className="card">
              <p className="empty">
                <strong>{tab === 'inbox' ? 'Nothing in the inbox' : 'Nothing sent yet'}</strong>
                {tab === 'inbox'
                  ? 'Replies will appear here once a mailbox is connected.'
                  : 'Messages you send from the Compose tab are kept here, filed against the account they went to.'}
              </p>
            </div>
          ) : (
            <ul className="msg-list">
              {messages.map(m => (
                <MessageRow
                  key={m.id}
                  message={m}
                  folder={tab}
                  useTeamColors={settings.use_team_colors}
                  onOpen={openMessage}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default Mail;
